import { forwardRef, useState, useRef } from 'react';
import { format } from 'date-fns';
import { Cat } from 'lucide-react';
import { cn } from '@/lib/utils';
import { WeeklyTask } from '@/hooks/useWeeklyTasks';
import { WeeklyTaskSlotCard } from './WeeklyTaskSlotCard';

interface MobileDayViewProps {
  hours: number[];
  dayKeyEn: string;
  selectedDay: Date;
  weekOffset: number;
  getTasksForDayAndHour: (dayKeyEn: string, hour: number) => (WeeklyTask | any)[];
  onEditTask: (task: WeeklyTask) => void;
  users: { id: string; name: string; color: string; textColor: string }[];
  hideEmptyHours?: boolean;
  onSwipe?: (direction: 'prev' | 'next') => void;
}

export const MobileDayView = forwardRef<HTMLDivElement, MobileDayViewProps>(
  ({ hours, dayKeyEn, selectedDay, weekOffset, getTasksForDayAndHour, onEditTask, users, hideEmptyHours = false, onSwipe }, ref) => {
    const isToday = format(selectedDay, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
    const currentHour = new Date().getHours();
    
    // Swipe handling
    const touchStartX = useRef<number | null>(null);
    const touchStartY = useRef<number | null>(null);
    const [swipeOffset, setSwipeOffset] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    
    const handleTouchStart = (e: React.TouchEvent) => {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
      setIsAnimating(false);
    };
    
    const handleTouchMove = (e: React.TouchEvent) => {
      if (touchStartX.current === null || touchStartY.current === null) return;
      
      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const diffX = currentX - touchStartX.current;
      const diffY = currentY - touchStartY.current;
      
      // Only handle horizontal swipes (ignore vertical scrolling)
      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 10) {
        setSwipeOffset(diffX * 0.3); // Dampen the offset for visual feedback
      }
    };
    
    const handleTouchEnd = () => {
      if (touchStartX.current === null) return;
      
      const threshold = 50; // Minimum swipe distance
      
      if (Math.abs(swipeOffset) > threshold && onSwipe) {
        setIsAnimating(true);
        // RTL: swipe right = previous day, swipe left = next day
        if (swipeOffset > 0) {
          onSwipe('prev');
        } else {
          onSwipe('next');
        }
      }
      
      setSwipeOffset(0);
      touchStartX.current = null;
      touchStartY.current = null;
    };

    // Filter hours based on hideEmptyHours
    const filteredHours = hideEmptyHours 
      ? hours.filter(hour => {
          const tasks = getTasksForDayAndHour(dayKeyEn, hour);
          const isCurrentHour = isToday && currentHour === hour;
          return tasks.length > 0 || isCurrentHour;
        })
      : hours;

    return (
      <div 
        ref={ref}
        className={cn(
          "flex-1 overflow-y-auto bg-slate-900",
          isAnimating && "transition-transform duration-200"
        )}
        style={{ transform: `translateX(${swipeOffset}px)` }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {filteredHours.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <Cat className="w-12 h-12 mb-3 opacity-30" />
            <p>אין משימות ביום זה</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-700/50">
            {filteredHours.map((hour) => {
              const tasks = getTasksForDayAndHour(dayKeyEn, hour);
              const isCurrentHour = isToday && currentHour === hour;
              const hasTasks = tasks.length > 0;

              return (
                <div 
                  key={hour}
                  id={`mobile-hour-${hour}`}
                  className={cn(
                    "flex transition-all",
                    hasTasks ? "min-h-[120px]" : "min-h-[48px]",
                    isCurrentHour && "bg-skylight-blue/10"
                  )}
                >
                {/* Hour Label */}
                <div className={cn(
                  "w-14 shrink-0 py-2 px-1 text-center border-l border-slate-700",
                  isCurrentHour ? "bg-skylight-blue/20" : "bg-slate-800/50"
                )}>
                  <div className={cn(
                    "text-sm font-medium",
                    isCurrentHour ? "text-skylight-blue" : "text-slate-400"
                  )}>
                    {hour.toString().padStart(2, '0')}:00
                  </div>
                </div>

                {/* Tasks Area */}
                <div className={cn(
                  "flex-1 p-2",
                  hasTasks && "space-y-2"
                )}>
                  {tasks.map((task: any) => {
                    const isGoogleEvent = task.source === 'google';
                    const user = users.find(u => u.id === task.user_name);
                    
                    if (isGoogleEvent) {
                      return (
                        <div
                          key={`google-${task.id}`}
                          className="p-3 rounded-xl opacity-80 animate-fade-in"
                          style={{
                            backgroundColor: `${user?.color}15`,
                            borderRight: `4px solid ${user?.color}`
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <Cat className="h-4 w-4" style={{ color: user?.color }} />
                            <div className={cn("font-medium text-sm", user?.textColor)}>
                              {task.summary || task.title || 'אירוע Google'}
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    return (
                      <WeeklyTaskSlotCard
                        key={task.id}
                        task={task}
                        user={user}
                        hour={hour}
                        weekOffset={weekOffset}
                        onEditClick={onEditTask}
                      />
                    );
                  })}
                </div>
              </div>
            );
              })}
          </div>
        )}

        {/* Bottom padding for drawer */}
        <div className="h-20" />
      </div>
    );
  }
);

MobileDayView.displayName = 'MobileDayView';
